/**
 * Drop-in replacement for usePlacesWidget from react-google-autocomplete.
 *
 * Key differences:
 *   - No apiKey parameter — the key lives in googleMapsLoader (loaded once).
 *   - Uses @googlemaps/js-api-loader so the script URL includes loading=async,
 *     eliminating the "loaded directly without loading=async" warning.
 *   - Still uses google.maps.places.Autocomplete under the hood (deprecated but
 *     functional — see googleMapsLoader.ts for the migration note).
 */

import { useEffect, useRef } from 'react';
import { loadGoogleMaps } from '../services/googleMapsLoader';

export interface PlacesWidgetOptions {
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  options?: {
    types?: string[];
    fields?: string[];
    componentRestrictions?: { country: string | string[] };
    bounds?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;
    strictBounds?: boolean;
  };
}

export function usePlacesWidget<T extends HTMLInputElement = HTMLInputElement>(
  config: PlacesWidgetOptions
) {
  const ref = useRef<T>(null);

  // Keep callback in a ref so the effect only runs on mount/unmount.
  const callbackRef = useRef(config.onPlaceSelected);
  useEffect(() => { callbackRef.current = config.onPlaceSelected; });

  // Freeze options at mount time — Autocomplete options are set once on the
  // constructor and cannot be changed afterwards, matching the old library's
  // behaviour.
  const optionsRef = useRef(config.options);

  useEffect(() => {
    let cancelled = false;
    let autocomplete: google.maps.places.Autocomplete | null = null;
    let listener: google.maps.MapsEventListener | null = null;

    loadGoogleMaps().then(() => {
      if (cancelled || !ref.current) return;

      autocomplete = new google.maps.places.Autocomplete(ref.current, {
        types: optionsRef.current?.types,
        fields: optionsRef.current?.fields,
        componentRestrictions: optionsRef.current?.componentRestrictions,
        bounds: optionsRef.current?.bounds,
        strictBounds: optionsRef.current?.strictBounds,
      });

      listener = autocomplete.addListener('place_changed', () => {
        if (autocomplete) callbackRef.current(autocomplete.getPlace());
      });
    });

    return () => {
      cancelled = true;
      if (listener) google.maps.event.removeListener(listener);
      if (autocomplete) google.maps.event.clearInstanceListeners(autocomplete);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once per mount

  return { ref };
}
